import {
  useWSModalCustom,
  WSCard,
  WSDivider,
  WSList,
  WSModal,
  WSModalCustomProps
} from "@wingspanhq/fe-component-library";
import { InsuranceCoverageMonitorSettingHelper } from "@wingspanhq/benefits/dist/src/lib/helpers/insuranceCoverageMonitorSettingHelper";
import { AIValidationWidget } from "../../../components/AIValidationWidget";
import { RequirementContextType } from "../../../types";
import { ValidationItemProps } from "../../../components/AIValidationWidget/ValidationItem";
import type {
  insuranceCoverageApiSpec,
  insuranceMonitorApiSpec
} from "@wingspanhq/wingspan-api-types";

import {
  SharedFileRequestJobStatus,
  SharedFileRequestJobType
} from "@wingspanhq/shared-files/dist/lib/interfaces";
import { DataViewGrid } from "../../../../../shared/components/DataView";

type InsuranceCoverageMonitor =
  insuranceMonitorApiSpec.InsuranceCoverageMonitor;

type Props = WSModalCustomProps<void> & {
  item: insuranceCoverageApiSpec.InsuranceCoverage;
  coverageMonitor: InsuranceCoverageMonitor;
};

export const ModalValidationDetails: React.FC<Props> = props => {
  let validationItemsMap: Record<string, ValidationItemProps[]> = {};
  const coverages = props.coverageMonitor.settings?.coverages;

  if (!coverages) {
    return null;
  }

  for (const coverage of coverages) {
    // instantiate the helper to validate coverage against the settings
    const validationHelper = new InsuranceCoverageMonitorSettingHelper(
      coverage as any
    );

    // validate the coverage
    const validationResults = validationHelper.validateCoverage(
      props.item as any
    );

    // get the validation result to create the map
    const validationJobs = validationResults.map(validationResult => {
      return {
        title: validationResult.title,
        description: validationResult.description,
        type: validationResult.type,
        status: validationResult.status,
        statusReason: validationResult.statusReason,
        groupKey: validationResult.policyType
      };
    });

    // create the map by groupKey
    validationJobs.forEach(validationJob => {
      if (!validationItemsMap[validationJob.groupKey]) {
        validationItemsMap[validationJob.groupKey] = [];
      }
      validationItemsMap[validationJob.groupKey].push(validationJob as any);
    });
  }

  return (
    <WSModal
      title="Validation details"
      actions={{
        alignment: "fill",
        buttons: [
          {
            label: "Done",
            onClick: () => {
              props.onClose();
            }
          }
        ]
      }}
      {...props}
    >
      <WSList gap="XL">
        {/* Header card */}
        <WSCard>
          <DataViewGrid
            gridColumns={2}
            data={[
              {
                label: "Policy",
                value: props.item.title || props.item.policyNumber
              },
              {
                label: "Matching Results",
                pill: {
                  theme: "warning",
                  icon: true,
                  text: "Partial match"
                },
                value: null,
                hideValue: true
              }
            ]}
          />
        </WSCard>
        {/* the validations jobs */}
        {/** Show first validationItemMap with title */}
        {Object.values(validationItemsMap)[0] && (
          <AIValidationWidget
            key={0}
            contextType={RequirementContextType.Payee}
            validationItems={Object.values(validationItemsMap)[0]}
          />
        )}

        {/** Show rest of validationItemsMap */}
        {Object.values(validationItemsMap).length > 1 &&
          Object.values(validationItemsMap)
            .slice(1)
            .map((validationItems, index) => {
              return (
                <WSList key={index} gap="L">
                  {/**Show OR before validation widget */}
                  <WSDivider label="OR" />
                  <AIValidationWidget
                    hideTitle
                    key={index}
                    contextType={RequirementContextType.Payee}
                    validationItems={validationItems}
                  />
                </WSList>
              );
            })}
      </WSList>
    </WSModal>
  );
};

export const useModalValidationDetails = () =>
  useWSModalCustom(ModalValidationDetails);
